import { useState, useEffect, useRef } from 'react';
import { Send, RotateCcw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

export const AIInteractionSection = () => {
  const [currentInputText, setCurrentInputText] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showSendButton, setShowSendButton] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [isDemoPlaying, setIsDemoPlaying] = useState(false);
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [hasPlayedDemo, setHasPlayedDemo] = useState(false);
  
  const animationRef = useRef(null);
  const timeoutRef = useRef(null);
  const chatContainerRef = useRef(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const sectionRef = useRef(null);

  const promptQuestion = "Tell me how hirerkey saves time?";
  const responseText = `Here's how hirerkey saves time for HRs:

1. **Appraisals done in seconds**
Create quarterly, half-yearly, and annual appraisals for every department and grade — all generated by AI with ready forms, ratings, and summaries.

2. **Smarter hiring, less screening**
Post a JD and let AI read every resume, score candidates, highlight missing skills, and show you the best matches instantly. No more manual filtering.

3. **Instant onboarding setup**
Design onboarding journeys and department tours in under 3 seconds. New joiners see exactly what they need — videos, tasks, and introductions — right on their phones.

4. **Exit interviews with AI insights**
Collect exit responses and let AI highlight themes, trends, and pain points with data-driven nudges on what's working and what's not.

5. **Expenses that manage themselves**
Employees just upload or click a photo of their invoice — AI reads, categorizes, and sends it to managers and finance for instant approval.

**You can now type your question and ask me anything.**`;

  const playfulResponse = `I knew you'd try that — but this is just a demo of hirerkey's charm. The actual magic starts once you implement it at your workplace.

If this made you smile, leave a 👍 in the prompt window so we know it worked.`;

  const thumbsUpResponse = `Haha! We saw that 👍 — and honestly? It made our day.

Here's the thing: hirerkey isn't just smart. It's built to make work feel less like work. From AI-driven appraisals to expenses that practically approve themselves, it's designed to give HRs their time back.

**Want to see it in action at your workplace?** Let's talk.`;

  const startDemoAnimation = async () => {
    setIsDemoPlaying(true);
    setIsTyping(true);
    setIsInputDisabled(true);
    setCurrentInputText('');
    setAnimationComplete(false);
    setChatMessages([]);

    // Small initial delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Directly add user's demo question as a bubble (no typing in input)
    setChatMessages([{ type: 'user', text: promptQuestion }]);
    scrollToBottom();
    
    // Wait a bit before response starts
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Type the response with live updates
    let typedResponse = '';
    for (let i = 0; i <= responseText.length; i++) {
      typedResponse = responseText.slice(0, i);
      setChatMessages(prev => {
        const newMessages = [...prev];
        if (newMessages.length > 0 && newMessages[newMessages.length - 1]?.type === 'ai' && newMessages[newMessages.length - 1]?.isTyping) {
          newMessages[newMessages.length - 1].text = typedResponse;
        } else {
          newMessages.push({ type: 'ai', text: typedResponse, isTyping: true });
        }
        return newMessages;
      });
      
      // Auto-scroll during typing so all content is visible
      if (i % 50 === 0 || i === responseText.length) {
        scrollToBottom();
      }
      
      await new Promise(resolve => timeoutRef.current = setTimeout(resolve, 20));
    }
    
    // Mark typing as complete
    setChatMessages(prev => {
      const newMessages = [...prev];
      if (newMessages[newMessages.length - 1]?.isTyping) {
        delete newMessages[newMessages.length - 1].isTyping;
      }
      return newMessages;
    });
    
    setIsTyping(false);
    setAnimationComplete(true);
    setIsDemoPlaying(false);
    setIsInputDisabled(false);
    scrollToBottom();
  };

  const handleUserSubmit = async () => {
    if (!currentInputText.trim() || isTyping || isInputDisabled) return;

    const userMessage = currentInputText.trim();
    setCurrentInputText('');
    
    // Add user message to chat
    setChatMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    
    // Small delay for message to appear, then scroll to show it
    await new Promise(resolve => setTimeout(resolve, 100));
    scrollToBottom();
    
    // Wait a bit more before AI response
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Determine response based on input
    let aiResponse = playfulResponse;
    const lowerInput = userMessage.toLowerCase();
    if (lowerInput.includes('👍') || lowerInput.includes('thumbs up') || lowerInput === '👍') {
      aiResponse = thumbsUpResponse;
    }
    
    // Start typing AI response
    setIsTyping(true);
    let typedResponse = '';
    
    for (let i = 0; i <= aiResponse.length; i++) {
      typedResponse = aiResponse.slice(0, i);
      setChatMessages(prev => {
        const newMessages = [...prev];
        if (newMessages[newMessages.length - 1]?.type === 'ai' && newMessages[newMessages.length - 1]?.isTyping) {
          newMessages[newMessages.length - 1].text = typedResponse;
        } else {
          newMessages.push({ type: 'ai', text: typedResponse, isTyping: true });
        }
        return newMessages;
      });
      
      // Auto-scroll periodically during typing
      if (i % 30 === 0 || i === aiResponse.length) {
        scrollToBottom();
      }
      
      await new Promise(resolve => setTimeout(resolve, 20));
    }
    
    // Mark typing as complete
    setChatMessages(prev => {
      const newMessages = [...prev];
      if (newMessages[newMessages.length - 1]?.isTyping) {
        delete newMessages[newMessages.length - 1].isTyping;
      }
      return newMessages;
    });
    
    setIsTyping(false);
    scrollToBottom();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleUserSubmit();
    }
  };

  const handleReplay = () => {
    setHasPlayedDemo(false); // Allow demo to play again
    startDemoAnimation();
  };

  const scrollToBottom = () => {
    // Scroll only the chat container, not the page
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // Intersection Observer to detect when section is in viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasPlayedDemo) {
            // Section is in viewport and demo hasn't played yet
            setHasPlayedDemo(true);
            startDemoAnimation();
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [hasPlayedDemo]);

  const renderFormattedText = (text) => {
    return text.split('\n').map((line, index) => {
      // First handle bold (**text**), then handle remaining italic (*text*)
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const renderedParts = [];
      
      parts.forEach((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          renderedParts.push(
            <strong key={`b-${index}-${i}`} className="font-bold text-white">
              {part.slice(2, -2)}
            </strong>
          );
        } else {
          // Handle italics in remaining text
          const italicParts = part.split(/(\*[^*]+\*)/g);
          italicParts.forEach((iPart, j) => {
            if (iPart && iPart.startsWith('*') && iPart.endsWith('*')) {
              renderedParts.push(
                <em key={`i-${index}-${i}-${j}`} className="italic text-gray-400">
                  {iPart.slice(1, -1)}
                </em>
              );
            } else if (iPart) {
              renderedParts.push(
                <span key={`s-${index}-${i}-${j}`}>{iPart}</span>
              );
            }
          });
        }
      });
      
      return (
        <div key={index} className={line.trim() === '' ? 'h-4' : 'mb-3'}>
          {renderedParts}
        </div>
      );
    });
  };

  return (
    <section ref={sectionRef} className="py-8 sm:py-10 md:py-12 bg-gradient-subtle relative overflow-hidden">
      <div className="max-w-[745px] w-full mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3">
            See <span className="emphasize">hirerkey AI</span> in Action
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground">
            Experience how AI makes workplace management effortless
          </p>
        </div>

        {/* ChatGPT-like Interface */}
        <Card className="bg-[#2f2f2f] border-[#4a4a4a] overflow-hidden shadow-md">
          {/* Header with logo */}
          <div className="bg-[#202020] px-4 py-3 flex items-center justify-between border-b border-[#4a4a4a]">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_profile-audit/artifacts/daspifu4_logo-dark.png"
                alt="hirerkey"
                className="h-6 w-auto"
              />
              <span className="text-white text-sm font-medium">Ask hirerkey anything</span>
            </div>
            
            {/* Control buttons */}
            <div className="flex space-x-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleReplay}
                className="text-white hover:bg-[#3a3a3a] h-8 px-3"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Chat Area */}
          <div ref={chatContainerRef} className="p-4 sm:p-6 space-y-6 min-h-[400px] max-h-[600px] overflow-y-auto bg-[#212121]">
            {/* Chat Messages */}
            {chatMessages.map((message, index) => (
              <div key={index} className="flex items-start space-x-3 animate-fade-in">
                <div className={`w-8 h-8 rounded-full ${message.type === 'user' ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-purple-500 to-pink-500'} flex items-center justify-center flex-shrink-0`}>
                  <span className="text-white text-sm font-semibold">{message.type === 'user' ? 'U' : 'AI'}</span>
                </div>
                <div className="flex-1">
                  {message.type === 'user' ? (
                    <div className="bg-[#3a3a3a] rounded-lg px-4 py-3 border border-[#4a4a4a] inline-block">
                      <p className="text-white text-sm">{message.text}</p>
                    </div>
                  ) : (
                    <div className="text-gray-300 text-sm leading-relaxed">
                      {renderFormattedText(message.text)}
                      {message.isTyping && <span className="inline-block w-1 h-4 bg-white ml-1 animate-pulse"></span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            <div ref={chatEndRef} />
          </div>

          {/* Single Input Area at Bottom */}
          <div className="bg-[#2f2f2f] px-4 py-3 border-t border-[#4a4a4a]">
            <div className="flex items-center space-x-3">
              <input
                ref={inputRef}
                type="text"
                value={currentInputText}
                onChange={(e) => !isInputDisabled && setCurrentInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isInputDisabled ? "Demo in progress..." : "Try asking something..."}
                disabled={isInputDisabled}
                className="flex-1 bg-[#3a3a3a] text-white text-sm rounded-lg px-4 py-3 outline-none placeholder:text-gray-400 border border-[#4a4a4a] focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                onClick={handleUserSubmit}
                disabled={!currentInputText.trim() || isTyping || isInputDisabled}
                className="text-white hover:text-blue-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default AIInteractionSection;
